import { useEffect, useRef } from 'react';

import { active } from 'src/components/languagePopup/LanguagePopupStyles';
import {
  Duration,
  Option,
  OptionState,
  SubscriptionState
} from 'src/types/subscription';
import { mixpanelObject } from 'src/utils/analytics';
import {
  MIXPANEL_COUNTER,
  MIXPANEL_UNIQUE_ID
} from 'src/utils/analytics/mixpanel/mixpanelConstants';
import { packToggleEvent } from 'src/utils/analytics/mixpanel/mixpanelEventsHelper';
import { getLocalStorage } from 'src/utils/browserStorageHandler';
import { ActivePromotions, DynamicObject } from 'src/utils/commonTypes';
import { GUEST_TOKEN } from 'src/utils/constants/localStorageConstants';
import {
  calculateSellPriceNew,
  getCurrencySymbol,
  trackSelectedPlanMixpanelEvents
} from 'src/utils/SubscriptionHelper/SubscriptionHelper';

import FeatureCard from './Feature';
import PlanCard from './PlanCard';
import {
  whitePill,
  goldPill,
  Container,
  FeaturesContainer,
  Header,
  Pill,
  PillsContainer,
  PlanCardContainer,
  transparentPill,
  premiumContainer,
  Description
} from './styles';
import { useAppStore } from '../../../hooks/useAppStore';
import {
  SUBSCRIPTION_ACTIONS,
  useSubscriptionDispatch,
  useSubscriptionState
} from '../SubscriptionContext';

const getTitle = (duration: Duration, translations: DynamicObject) => {
  return `${duration.value} ${translations[duration?.label]}`;
};

const getPillVariant = (active: boolean, isPremium: boolean) => {
  if (!active) return transparentPill;
  if (isPremium) return goldPill;
  return whitePill;
};

const PillsTray = ({
  data: durationList,
  isPremium = false,
  activeOption,
  packToggleCounter,
  activePromotions = {}
}: {
  data: OptionState[];
  isPremium?: boolean;
  activeOption?: OptionState;
  activePromotions: ActivePromotions;
  packToggleCounter: { current: number };
}) => {
  const {
    subscriptionData: currentSubscription,
    isUserLoggedIn,
    usersData,
    translations
  } = useAppStore();
  const dispatch = useSubscriptionDispatch();
  const activePillRef = useRef<HTMLButtonElement>(null);
  const triggerPackToggleEvents = () => {
    packToggleCounter.current += 1;
    mixpanelObject.setProperty(MIXPANEL_COUNTER, packToggleCounter.current);
    mixpanelObject.setProperty(
      MIXPANEL_UNIQUE_ID,
      isUserLoggedIn ? usersData?.id : getLocalStorage(GUEST_TOKEN)
    );
    packToggleEvent();
  };

  const handlePillClick = (id: string, clickAllowed: boolean) => () => {
    trackSelectedPlanMixpanelEvents(activeOption, !!currentSubscription);
    if (!clickAllowed) return;
    triggerPackToggleEvents();
    dispatch({ type: SUBSCRIPTION_ACTIONS.UPDATE_DURATION, payload: { id } });
  };

  const SubscriptionTranslations = translations?.subscription || {};
  useEffect(() => {
    if (activePillRef.current) {
      activePillRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'center'
      });
    }
  }, [durationList]);

  return (
    <PillsContainer data-test-id="plan-duration-container">
      {durationList?.map((duration) => {
        const { price } = duration;
        const { currency } = price;
        const currencySymbol = getCurrencySymbol(currency);
        const title = `${getTitle(
          duration.duration,
          SubscriptionTranslations
        )} ${currencySymbol} ${
          duration ? calculateSellPriceNew(duration, activePromotions) : null
        }/-`;
        return (
          <Pill
            data-test-id={`plan-duration${active ? '-active' : ''}_${
              duration.id
            }`}
            ref={duration.active ? activePillRef : null}
            css={getPillVariant(duration.active, isPremium)}
            onClick={handlePillClick(duration.id, duration.purchaseAllowed)}
            key={duration.id}>
            {title}
          </Pill>
        );
      })}
    </PillsContainer>
  );
};

const PlanCardTray = ({
  data: planList,
  packToggleCounter,
  activePromotions
}: {
  data: SubscriptionState[];
  packToggleCounter: { current: number };
  activePromotions: ActivePromotions;
}) => {
  const activePlanRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (activePlanRef.current) {
      activePlanRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'center'
      });
    }
  }, [planList]);
  return (
    <PlanCardContainer>
      {planList.map((item) => (
        <PlanCard
          key={item.title}
          data={item}
          planRef={item.active ? activePlanRef : undefined}
          packToggleCounter={packToggleCounter}
          activePromotions={activePromotions}
        />
      ))}
    </PlanCardContainer>
  );
};

const FeaturesTray = ({ data }: { data: Option }) => {
  const { features: featuresList = [], deviceCount = 1 } = data;
  return (
    <FeaturesContainer>
      {featuresList.map((item) => (
        <FeatureCard key={item.text} deviceCount={deviceCount} {...item} />
      ))}
    </FeaturesContainer>
  );
};

const Plans = ({
  activePromotions
}: {
  activePromotions: ActivePromotions;
}) => {
  const data = useSubscriptionState();
  const { translations } = useAppStore();
  const packToggleCounter = useRef(0);
  if (!data) return <></>;
  const activeCategory = data.find((item) => item.active);
  const options = activeCategory?.options ?? [];
  const activeOption = options.find((item) => item.active);

  const NEW_SUBSCRIPTION_STRINGS = translations.subscription;
  const headerText = activeCategory?.title ?? '';
  const defaultDescriptionKey = 'CMS_PLAN_DESCRIPTION_DEFAULT_KEY';
  const descriptionText =
    NEW_SUBSCRIPTION_STRINGS[
      activeOption?.description ?? defaultDescriptionKey
    ] ?? activeOption?.description;
  const isPremium = activeCategory?.isPremium;

  return (
    <Container
      data-test-id="plans-container"
      css={[isPremium && premiumContainer]}>
      <Header dat-test-id="plan-title">{headerText}</Header>
      <Description data-test-id="plan-description">
        {descriptionText}
      </Description>
      {activeOption ? <FeaturesTray data={activeOption} /> : null}
      <PlanCardTray
        packToggleCounter={packToggleCounter}
        data={data}
        activePromotions={activePromotions}
      />
      <PillsTray
        packToggleCounter={packToggleCounter}
        activeOption={activeOption}
        data={options}
        isPremium={activeCategory?.isPremium}
        activePromotions={activePromotions}
      />
    </Container>
  );
};

export default Plans;
